/**
 * Meal crate configuration for fulfilment stock requirements and related ops flows.
 *
 * Copied from lyka-shared-types/src/types/models/pouchesPerCrate.ts.
 * Fulfilment services should import from @lyka-pet-food/contracts; the shared-types copy
 * is legacy until all callers migrate.
 */
import { z } from 'zod'

export const LYKA_POUCH_SIZES = ['125', '150', '200', '250', '300', '400', '500', '600', '700'] as const
export const TYGA_POUCH_SIZES = ['170'] as const
export const POUCH_SIZES = [...LYKA_POUCH_SIZES, ...TYGA_POUCH_SIZES] as const

export const LykaPouchSize = z.enum(LYKA_POUCH_SIZES)
export type LykaPouchSize = z.infer<typeof LykaPouchSize>

export const TygaPouchSize = z.enum(TYGA_POUCH_SIZES)
export type TygaPouchSize = z.infer<typeof TygaPouchSize>

export const PouchSize = z.enum(POUCH_SIZES)
export type PouchSize = z.infer<typeof PouchSize>

export const PROTEINS = [
  'hypoallergenic pro',
  'goat',
  'fish',
  'turkey',
  'lamb',
  'beef',
  'chicken',
  'kangaroo',
  'pork',
] as const
export const Protein = z.enum(PROTEINS)
export type Protein = z.infer<typeof Protein>

export const RECIPE_TYPES = ['smooth', 'chunky'] as const
export const RecipeType = z.enum(RECIPE_TYPES)
export type RecipeType = z.infer<typeof RecipeType>

export const PouchesPerCrateSchema = z.record(LykaPouchSize, z.number())
export type PouchesPerCrate = z.infer<typeof PouchesPerCrateSchema>

export const ProteinConfigurationSchema = z.object({
  recipeType: RecipeType,
  pouchesPerCrate: PouchesPerCrateSchema,
})
export type ProteinConfiguration = z.infer<typeof ProteinConfigurationSchema>

export const ProteinConfigurationsSchema = z.record(Protein, ProteinConfigurationSchema)
export type ProteinConfigurations = z.infer<typeof ProteinConfigurationsSchema>

export const POUCHES_PER_CRATE = {
  '125': 80,
  '150': 75,
  '170': 75,
  '200': 60,
  '250': 45,
  '300': 36,
  '400': 30,
  '500': 24,
  '600': 18,
  '700': 17,
} as const satisfies Record<PouchSize, number>

const lykaPouchesPerCrate = Object.fromEntries(
  LYKA_POUCH_SIZES.map((size) => [size, POUCHES_PER_CRATE[size]]),
) as PouchesPerCrate

const RECIPE_TYPE_BY_PROTEIN: Record<Protein, RecipeType> = {
  goat: 'chunky',
  'hypoallergenic pro': 'chunky',
  fish: 'chunky',
  turkey: 'chunky',
  lamb: 'chunky',
  beef: 'chunky',
  chicken: 'chunky',
  kangaroo: 'chunky',
  pork: 'chunky',
}

export const proteinConfiguration: ProteinConfigurations = Object.fromEntries(
  PROTEINS.map((protein) => [
    protein,
    { recipeType: RECIPE_TYPE_BY_PROTEIN[protein], pouchesPerCrate: lykaPouchesPerCrate },
  ]),
) as ProteinConfigurations

export const TygaPouchesPerCrateSchema = z.record(TygaPouchSize, z.number())
export type TygaPouchesPerCrate = z.infer<typeof TygaPouchesPerCrateSchema>

export const tygaPouchesPerCrate: TygaPouchesPerCrate = {
  '170': POUCHES_PER_CRATE['170'],
}
